import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { bookingApi } from '../../utils/bookingApi';
import { PageHeader, Skeleton, EmptyState, ErrorState } from '../../components/ui';
import { formatDate, formatDayDate } from '../../utils/dates';
import './booking-shared.css';

const INCIDENT_TYPE_LABEL = {
  INJURY: 'Injury',
  NEAR_MISS: 'Near miss',
  ILLNESS: 'Illness',
  OTHER: 'Other',
};

const SEVERITY = {
  MINOR: { label: 'Minor', tone: 'good' },
  MODERATE: { label: 'Moderate', tone: 'wait' },
  SEVERE: { label: 'Severe', tone: 'bad' },
};

const LIST = { to: '/booking/incidents', label: 'First aid reports' };

function SeverityPill({ severity }) {
  const s = SEVERITY[severity] || { label: severity, tone: 'off' };
  return <span className={`bk-pill bk-pill--${s.tone}`}>{s.label}</span>;
}

const gymnastName = (incident) => (incident.gymnast
  ? `${incident.gymnast.firstName} ${incident.gymnast.lastName}`
  : 'Unknown');

// The family's reports, loaded once; the list and each report's page share it
function useMyIncidents() {
  const [incidents, setIncidents] = useState(null);
  const [failed, setFailed] = useState(false);
  const load = useCallback(() => {
    setFailed(false);
    setIncidents(null);
    bookingApi.getMyIncidents()
      .then(res => setIncidents(res.data))
      .catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);
  return { incidents, failed, load };
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <h2 className="bk-section-title">{title}</h2>
      {children}
    </div>
  );
}

// One report on its own page (/booking/incidents/:id)
export function MyIncidentDetail() {
  const { id } = useParams();
  const { incidents, failed, load } = useMyIncidents();
  const incident = incidents?.find(i => i.id === id);

  if (failed) {
    return (
      <div className="bk-page bk-page--narrow">
        <PageHeader back={LIST} title="First aid report" />
        <ErrorState title="Couldn't load this report" onRetry={load} />
      </div>
    );
  }
  if (!incidents) {
    return (
      <div className="bk-page bk-page--narrow">
        <PageHeader back={LIST} title="First aid report" />
        <Skeleton rows={3} label="Loading the report…" />
      </div>
    );
  }
  if (!incident) {
    return (
      <div className="bk-page bk-page--narrow">
        <PageHeader back={LIST} title="First aid report" />
        <EmptyState icon={ClipboardDocumentListIcon} title="Couldn't find this report" text="It may have been removed. Your other reports are in the list." />
      </div>
    );
  }

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader
        back={LIST}
        title="First aid report"
        details={`${gymnastName(incident)} · ${formatDayDate(incident.incidentDate)}`}
      />
      <div className="bk-card">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
          <div>
            <p className="bk-small" style={{ margin: 0 }}>Type</p>
            <p style={{ margin: 0 }}>{INCIDENT_TYPE_LABEL[incident.incidentType] ?? incident.incidentType}</p>
          </div>
          <div>
            <p className="bk-small" style={{ margin: 0 }}>Severity</p>
            <SeverityPill severity={incident.severity} />
          </div>
          {incident.location && (
            <div style={{ gridColumn: '1 / -1' }}>
              <p className="bk-small" style={{ margin: 0 }}>Location</p>
              <p style={{ margin: 0 }}>{incident.location}</p>
            </div>
          )}
        </div>

        <Section title="What happened">
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{incident.description}</p>
        </Section>
        {incident.injuryDetails && (
          <Section title="Injury or symptoms">
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{incident.injuryDetails}</p>
          </Section>
        )}
        {incident.firstAidGiven && (
          <Section title="First aid given">
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{incident.firstAidGiven}</p>
          </Section>
        )}
        {incident.outcome && (
          <Section title="Outcome">
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{incident.outcome}</p>
          </Section>
        )}

        <p className="bk-small" style={{ margin: '1rem 0 0' }}>
          Filed by {incident.reportedBy.firstName} {incident.reportedBy.lastName}
          {' · '}
          {incident.adultNotifiedAt
            ? `You were emailed on ${formatDate(incident.adultNotifiedAt)}`
            : 'Email notification pending'}
        </p>
      </div>
    </div>
  );
}

export default function MyIncidents() {
  const [searchParams] = useSearchParams();
  const { incidents, failed, load } = useMyIncidents();

  // The email about a new report links to ?id=; each report has its own page now
  const idFromUrl = searchParams.get('id');
  if (idFromUrl) return <Navigate to={`/booking/incidents/${idFromUrl}`} replace />;

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader title="First aid reports" />
      {failed ? (
        <ErrorState title="Couldn't load first aid reports" onRetry={load} />
      ) : !incidents ? (
        <Skeleton rows={3} label="Loading first aid reports…" />
      ) : incidents.length === 0 ? (
        <EmptyState icon={ClipboardDocumentListIcon} title="No first aid reports" text="If a coach records an injury or illness, the report shows here and you're emailed." />
      ) : (
        incidents.map(inc => (
          <Link key={inc.id} to={`/booking/incidents/${inc.id}`} className="bk-card" style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.4rem' }}>
              <div>
                <strong>{gymnastName(inc)}</strong>
                <span className="bk-small" style={{ marginLeft: '0.5rem' }}>{formatDayDate(inc.incidentDate)}</span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <SeverityPill severity={inc.severity} />
                <span className="bk-small">
                  {INCIDENT_TYPE_LABEL[inc.incidentType] ?? inc.incidentType}
                </span>
              </div>
            </div>
            <p className="bk-muted" style={{ margin: '0.35rem 0 0', fontSize: 'var(--text-body)' }}>
              {inc.description.length > 120 ? `${inc.description.slice(0, 120)}…` : inc.description}
            </p>
          </Link>
        ))
      )}
    </div>
  );
}
